import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getTestById } from '../services/testService';
import { Modal, Button, Spin } from 'antd';
import { HourglassOutlined } from '@ant-design/icons';
import axios from 'axios';
import './TakeTestPage.css';

const TakeTestPage = () => {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [timeDuration, setTimeDuration] = useState(60);
  const [timeLeft, setTimeLeft] = useState(3600);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAllAnswered, setIsAllAnswered] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const [violationModalVisible, setViolationModalVisible] = useState(false);
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  const [fullscreenPromptVisible, setFullscreenPromptVisible] = useState(false);
  const [isInFullscreen, setIsInFullscreen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testName, setTestName] = useState('');
  const [testNumber, setTestNumber] = useState('');
  const [testDetailsLoaded, setTestDetailsLoaded] = useState(false);
  const [currentNavPage, setCurrentNavPage] = useState(1);
  const [filterType, setFilterType] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [navigationSource, setNavigationSource] = useState(null);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);

  const contentRef = useRef(null);
  const violationLimit = 3;
  const violationCountRef = useRef(0);
  const questionsPerNavPage = 30;
  const hasSubmittedRef = useRef(false);

  const filteredIndices = useMemo(() => {
    return questions
      .map((_, i) => i)
      .filter((i) => {
        if (filterType === 'current') return i === currentIndex;
        if (filterType === 'answered') return answers[i] !== undefined;
        if (filterType === 'unanswered') return answers[i] === undefined;
        return true;
      });
  }, [questions, filterType, answers, currentIndex]);

  const totalNavPages = Math.ceil(filteredIndices.length / questionsPerNavPage);
  const startIndex = (currentNavPage - 1) * questionsPerNavPage;
  const endIndex = Math.min(startIndex + questionsPerNavPage, filteredIndices.length);
  const displayedIndices = filteredIndices.slice(startIndex, endIndex);

  const partialPayload = useMemo(() => {
    const studentData = JSON.parse(localStorage.getItem('student')) || {};
    return {
      student: studentId,
      testNumber: testNumber || testId,
      testName: testName || `Test ${testId}`,
      testId,
      questions: questions.map((q) => q._id || `q_${q.questionText}`), // Fallback for _id
      status: 'completed',
      results: answers,
      violationCount: violationCountRef.current,
      studentEmail: studentData.email || '',
      studentFullName: studentData.fullName || 'Student',
      timeDuration,
    };
  }, [studentId, testNumber, testName, testId, questions, answers, timeDuration]);

  const styles = {
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem 1.2rem',
      fontSize: '14px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      borderRadius: '1.5rem',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      whiteSpace: 'nowrap',
      fontFamily: "'Roboto', sans-serif",
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    modalButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalCancelButton: {
      backgroundColor: '#D1D5DB',
      color: '#374151',
      padding: '5px 15px',
      border: 'none',
      borderRadius: '30px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    indicatorBox: {
      current: { width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#1890ff', marginRight: '8px' },
      answered: { width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#32CD32', marginRight: '8px' },
      unanswered: { width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#D1D5DB', marginRight: '8px' },
    },
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
  };

  const mockGetTestById = async (testId) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          questions: [
            { _id: '1', questionText: 'What is 2 + 2?', options: ['3', '4', '5', '6'], correctAnswer: '4', section: 'Math' },
            { _id: '2', questionText: 'What is the capital of France?', options: ['Berlin', 'Madrid', 'Paris', 'Rome'], correctAnswer: 'Paris', section: 'Geography' },
          ],
          testName: `Test ${testId}`,
          testNumber: testId,
          timeDuration: 60,
        });
      }, 1000);
    });
  };

  useEffect(() => {
    const checkAccessAndTestStatus = async () => {
      const studentData = JSON.parse(localStorage.getItem('student'));
      if (!studentData?.id || !isValidObjectId(studentData.id)) {
        navigate('/login', { replace: true });
        return;
      }
      setStudentId(studentData.id);

      try {
        const response = await axios.get(`https://exam.doode.in/api/results/status`, { params: { testId, studentId: studentData.id } });
        if (response.data.isCompleted) {
          navigate('/studentmark-dashboard', {
            state: {
              message: 'This test has already been completed.',
              testName: response.data.results?.testName || `Test ${testId}`,
              score: response.data.results?.score,
              sectionStats: response.data.results?.sectionStats,
              questions: response.data.results?.questions,
              answers: response.data.results?.results,
            },
            replace: true,
          });
          return;
        }
      } catch (error) {
        console.error('Error checking test status:', error);
        navigate('/studentmark-dashboard', { state: { message: 'Error accessing test. Please try again later.' }, replace: true });
        return;
      }

      const pendingSubmission = localStorage.getItem('pendingTestSubmission');
      if (pendingSubmission) {
        const payload = JSON.parse(pendingSubmission);
        if (payload.testId === testId) {
          try {
            const response = await axios.post('https://exam.doode.in/api/test-attempt/submit', { ...payload, isAutoSubmit: true, violationType: 'Pending Submission Recovery' });
            if (response.status === 200) {
              localStorage.removeItem('pendingTestSubmission');
              navigate('/studentmark-dashboard', { state: { ...payload } });
            }
          } catch (error) {
            console.error('Failed to submit pending test:', error);
            localStorage.removeItem('pendingTestSubmission');
          }
        }
      }
    };
    checkAccessAndTestStatus();
  }, [testId, navigate]);

  const prepareSubmissionPayload = (violationType, isAutoSubmit) => {
    const timeTaken = startTime ? formatTime(Math.floor((new Date() - startTime) / 1000), 'email') : 'N/A';
    return {
      ...partialPayload,
      completedAt: new Date(),
      score: calculateScore(),
      sectionStats: calculateSectionStats(),
      isAutoSubmit,
      violationType,
      timeTaken,
    };
  };

  useEffect(() => {
    if (!isTestStarted || isSubmitting) return;
    const handleKeyDown = (event) => {
      if (event.altKey || event.key === 'Tab' || event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'r')) {
        event.preventDefault();
        if (!hasSubmittedRef.current) {
          hasSubmittedRef.current = true;
          setIsSubmitting(true);
          setIsAutoSubmitting(true);
          handleAutoSubmit('Tab Switch or Refresh');
        }
      }
    };
    const handleBeforeUnload = (event) => {
      if (isSubmitting || hasSubmittedRef.current) return;
      event.preventDefault();
      hasSubmittedRef.current = true;
      setIsSubmitting(true);
      setIsAutoSubmitting(true);
      const payload = prepareSubmissionPayload('Tab Switch or Refresh', true);
      localStorage.setItem('pendingTestSubmission', JSON.stringify(payload));
      handleAutoSubmit('Tab Switch or Refresh');
      event.returnValue = '';
    };
    const handleVisibilityChange = () => {
      if (document.hidden && !isSubmitting && !hasSubmittedRef.current) {
        hasSubmittedRef.current = true;
        setIsSubmitting(true);
        setIsAutoSubmitting(true);
        setViolationModalVisible(false);
        setFullscreenPromptVisible(false);
        handleAutoSubmit('Tab Switch');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTestStarted, isSubmitting, partialPayload, startTime]);

  const calculateScore = () => {
    let score = 0;
    questions.forEach((question, index) => {
      if (answers[index] === question.correctAnswer) score++;
    });
    return score;
  };

  const calculateSectionStats = () => {
    const sectionStats = {};
    questions.forEach((question, index) => {
      const selectedAnswer = answers[index];
      const isCorrect = selectedAnswer === question.correctAnswer;
      const section = question.section || 'General';
      if (!sectionStats[section]) sectionStats[section] = { correct: 0, incorrect: 0, skipped: 0, total: 0 };
      sectionStats[section].total++;
      if (selectedAnswer === undefined) sectionStats[section].skipped++;
      else if (isCorrect) sectionStats[section].correct++;
      else sectionStats[section].incorrect++;
    });
    return sectionStats;
  };

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    return () => { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto'; };
  }, [testId, location.key]);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        setLoading(true);
        const test = await getTestById(testId).catch(() => mockGetTestById(testId));
        setQuestions(test.questions || []);
        const extractedTestName = test.testName || test.name || test.title || `Test ${testId}`;
        const extractedTestNumber = test.testNumber || test.number || testId;
        const extractedTimeDuration = test.timeDuration || location.state?.testData?.timeDuration || 60;
        setTestName(extractedTestName);
        setTestNumber(extractedTestNumber);
        setTimeDuration(extractedTimeDuration);
        setTimeLeft(extractedTimeDuration * 60);
        setTestDetailsLoaded(true);
      } catch (err) {
        console.error('Error fetching test:', err);
        setTestName(`Test ${testId}`);
        setTestNumber(testId);
        setTimeDuration(60);
        setTimeLeft(3600);
        setTestDetailsLoaded(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId, location.state]);

  useEffect(() => {
    if (!isTestStarted || isSubmitting) return;
    const handleFullscreenChange = () => {
      const currentlyInFullscreen = !!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.msFullscreenElement;
      setIsInFullscreen(currentlyInFullscreen);
      if (!currentlyInFullscreen && !isSubmitting) {
        violationCountRef.current += 1;
        setViolationCount(violationCountRef.current);
        if (violationCountRef.current >= violationLimit) {
          setViolationModalVisible(false);
          setFullscreenPromptVisible(false);
          setIsAutoSubmitting(true);
          handleAutoSubmit('Full screen Violation');
        } else {
          setViolationModalVisible(true);
          setFullscreenPromptVisible(true);
        }
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [isTestStarted, isSubmitting]);

  useEffect(() => { violationCountRef.current = violationCount; }, [violationCount]);
  useEffect(() => {
    if (!isTestStarted || isSubmitting) return;
    if (timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    } else setIsModalVisible(true);
  }, [timeLeft, isTestStarted, isSubmitting]);

  const enterFullscreen = async () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) await elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) await elem.webkitRequestFullscreen();
      else if (elem.msRequestFullscreen) await elem.msRequestFullscreen();
      setFullscreenError(false);
      setIsInFullscreen(true);
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
      setFullscreenError(true);
      setFullscreenPromptVisible(true);
      setIsInFullscreen(false);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      else if (document.msExitFullscreen) await document.msExitFullscreen();
    } catch (error) { console.error('Failed to exit fullscreen:', error); }
  };

  const isDocumentInFullscreen = () => !!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.msFullscreenElement;

  const handleStartTest = () => {
    if (!window.location.protocol.includes('https') && window.location.hostname !== 'localhost') {
      Modal.error({ title: 'Secure Context Required', content: 'This test requires a secure context (HTTPS). Please ensure the page is loaded over HTTPS and try again.' });
      return;
    }
    setIsTestStarted(true);
    setStartTime(new Date());
    enterFullscreen();
  };

  const handleReenterFullscreen = () => { setFullscreenPromptVisible(false); enterFullscreen(); };
  const handleViolationModalOk = () => { setViolationModalVisible(false); if (!isInFullscreen) setFullscreenPromptVisible(true); };

  useEffect(() => {
    if (questions.length > 0) {
      const allAnswered = Object.keys(answers).length === questions.length && Object.keys(answers).every((index) => answers[index] !== undefined && answers[index] !== '');
      setIsAllAnswered(allAnswered);
    }
  }, [answers, questions]);

  const handleAnswerChange = (e) => {
    const newAnswers = { ...answers, [currentIndex]: e.target.value };
    setAnswers(newAnswers);
  };

  useEffect(() => {
    if (contentRef.current && isTestStarted && questions.length > 0 && currentIndex === 0 && !navigationSource) {
      contentRef.current.scrollTo({ top: 0 });
    }
  }, [isTestStarted, questions, currentIndex, navigationSource]);

  const goToQuestion = (index) => {
    setNavigationSource('navigator');
    setCurrentIndex(index);
    const page = Math.floor(index / questionsPerNavPage) + 1;
    setCurrentNavPage(page);
    if (contentRef.current) contentRef.current.scrollTo({ top: 0 });
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setNavigationSource('previous');
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const page = Math.floor(newIndex / questionsPerNavPage) + 1;
      setCurrentNavPage(page);
      if (contentRef.current) contentRef.current.scrollTo({ top: 0 });
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setNavigationSource('next');
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const page = Math.floor(newIndex / questionsPerNavPage) + 1;
      setCurrentNavPage(page);
      if (contentRef.current) contentRef.current.scrollTo({ top: 0 });
    }
  };

  const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);

  const formatTime = (seconds, formatType = 'timer') => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return formatType === 'email' ? `${mins}m ${secs}s` : `${String(mins)}:${String(secs).padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoading(true);
    try {
      if (!isValidObjectId(studentId)) {
        console.error('Invalid student ID');
        navigate('/studentmark-dashboard', { state: { message: 'Invalid student ID. Please log in again.' } });
        return;
      }
      const payload = prepareSubmissionPayload('Manual Submission', false);
      const response = await axios.post('https://exam.doode.in/api/test-attempt/submit', payload);
      if (response.status === 200) {
        if (isDocumentInFullscreen()) await exitFullscreen();
        localStorage.removeItem('pendingTestSubmission');
        navigate('/test-result', {
          state: {
            questions,
            answers,
            score: payload.score,
            sectionStats: payload.sectionStats,
            testName: payload.testName,
            message: response.data.message || 'Test submitted successfully! A confirmation email has been sent.',
          },
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      navigate('/studentmark-dashboard', { state: { message: error.response?.data?.message || 'Test submission failed.' } });
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSubmit = (violationType) => {
    setIsModalVisible(false);
    setViolationModalVisible(false);
    setFullscreenPromptVisible(false);
    const payload = prepareSubmissionPayload(violationType, true);
    axios.post('https://exam.doode.in/api/test-attempt/submit', payload)
      .then((response) => {
        if (response.status === 200) {
          if (isDocumentInFullscreen()) exitFullscreen();
          localStorage.removeItem('pendingTestSubmission');
          navigate('/test-result', {
            state: {
              questions,
              answers,
              score: payload.score,
              sectionStats: payload.sectionStats,
              testName: payload.testName,
              message: response.data.message || 'Test auto-submitted due to violation or refresh.',
            },
          });
        }
      })
      .catch((error) => {
        console.error('Auto-submit error:', error);
        navigate('/studentmark-dashboard', { state: { message: error.response?.data?.message || 'Test auto-submission failed.' } });
      })
      .finally(() => {
        setIsSubmitting(false);
        setIsAutoSubmitting(false);
        setLoading(false);
      });
  };

  const handleIndicatorClick = (type) => {
    setFilterType((prev) => {
      if (prev === type) {
        setNavigationSource('navigator');
        setCurrentIndex(0);
        setCurrentNavPage(1);
        return null;
      } else {
        const newIndices = questions.map((_, i) => i).filter((i) => {
          if (type === 'current') return i === currentIndex;
          if (type === 'answered') return answers[i] !== undefined;
          if (type === 'unanswered') return answers[i] === undefined;
          return true;
        });
        if (newIndices.length > 0) {
          setNavigationSource('navigator');
          setCurrentIndex(newIndices[0]);
          setCurrentNavPage(1);
        }
        return type;
      }
    });
  };

  if (loading || !testDetailsLoaded) return <div>Loading test...</div>;
  if (!isTestStarted) {
    return (
      <div className="start-screen">
        <h2>Welcome to: {testName}</h2>
        <p>Please click the button below to start the test. The test duration is {timeDuration} minutes. Switching tabs, using Alt+Tab, or refreshing the page will result in immediate submission. After {violationLimit} fullscreen violations, the test will also be automatically submitted.</p>
        <Button className="custom-button glow-button" style={styles.button} onClick={handleStartTest} disabled={loading}>Start Test</Button>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes glow { 0% { box-shadow: 0 0 5px rgba(107, 33, 168, 0.5); } 50% { box-shadow: 0 0 20px rgba(107, 33, 168, 0.8); } 100% { box-shadow: 0 0 5px rgba(107, 33, 168, 0.5); } }
          .glow-button:hover:not(:disabled) { background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF); color: #fff; transform: scale(1.05); animation: glow 1.5s ease-in-out infinite; }
          .modal-cancel-button:hover:not(:disabled) { background-color: #B0BEC5; color: #1F2937; }
          .indicator { cursor: pointer; display: flex; align-items: center; padding: 4px; }
          .indicator.selected { background-color: #F3F4F6; border-radius: 4px; }
        `}
      </style>
      <div className="main-container test-content">
        <div className="exam-card">
          <div className="fixed-header">
            <div className="header-left"><h4>{testName}</h4></div>
            <div className="header-center"><div className="violation-display">Violations: {violationCount}/{violationLimit}</div></div>
            <div className="header-right"><div className="timer-display"><HourglassOutlined className="sand-timer-icon" /> Time Left: {formatTime(timeLeft, 'timer')}</div></div>
          </div>
          <div className="content-wrapper">
            <div className="question-section">
              <div className="question-card">
                <div className="question-number">Question {currentIndex + 1}:</div>
                <div className="scrollable-content" ref={contentRef}>
                  {questions.length > 0 && (
                    <>
                      <p className="question-text">{questions[currentIndex].questionText}</p>
                      <div className="options-scrollable">
                        {Array.isArray(questions[currentIndex].options) && questions[currentIndex].options.length > 0 && questions[currentIndex].options.map((option, idx) => (
                          <div key={idx} className="option-card">
                            <label className="option-label">
                              <input
                                type="radio"
                                name={`question-${currentIndex}`}
                                value={option}
                                checked={answers[currentIndex] === option}
                                onChange={handleAnswerChange}
                                disabled={isSubmitting || isAutoSubmitting}
                              />
                              <span>{option}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="navigator-section">
              <h3 className="navigator-title">Navigator</h3>
              <div className="navigator-button-container">
                {displayedIndices.length > 0 ? displayedIndices.map((actualIndex) => (
                  <button
                    key={actualIndex}
                    onClick={() => goToQuestion(actualIndex)}
                    disabled={isSubmitting || isAutoSubmitting}
                    className={`navigator-button ${actualIndex === currentIndex ? 'current' : answers[actualIndex] ? 'answered' : 'unanswered'}`}
                  >
                    {actualIndex + 1}
                  </button>
                )) : Array.from({ length: questionsPerNavPage }, (_, i) => <div key={i} style={{ width: '32px', height: '32px' }}></div>)}
              </div>
              <div className="pagination-container">
                <button
                  className="custom-button pagination-button glow-button"
                  style={{ ...styles.button, ...(currentNavPage === 1 || isSubmitting || isAutoSubmitting ? styles.buttonDisabled : {}) }}
                  onClick={() => setCurrentNavPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentNavPage === 1 || isSubmitting || isAutoSubmitting}
                >
                  {'<'}
                </button>
                <button
                  className="custom-button pagination-button glow-button"
                  style={{ ...styles.button, ...(currentNavPage === totalNavPages || isSubmitting || isAutoSubmitting ? styles.buttonDisabled : {}) }}
                  onClick={() => setCurrentNavPage((prev) => Math.min(prev + 1, totalNavPages))}
                  disabled={currentNavPage === totalNavPages || isSubmitting || isAutoSubmitting}
                >
                  {'>'}
                </button>
              </div>
              <div className="indicator-container">
                <div className={`indicator ${filterType === 'current' ? 'selected' : ''}`} onClick={() => handleIndicatorClick('current')}>
                  <div style={styles.indicatorBox.current}></div><span className="indicator-text">Current</span>
                </div>
                <div className={`indicator ${filterType === 'answered' ? 'selected' : ''}`} onClick={() => handleIndicatorClick('answered')}>
                  <div style={styles.indicatorBox.answered}></div><span className="indicator-text">Answered</span>
                </div>
                <div className={`indicator ${filterType === 'unanswered' ? 'selected' : ''}`} onClick={() => handleIndicatorClick('unanswered')}>
                  <div style={styles.indicatorBox.unanswered}></div><span className="indicator-text">Unanswered</span>
                </div>
              </div>
            </div>
          </div>
          <div className="exam-footer">
            <div className="question-button-group">
              <Button
                className="custom-button glow-button"
                style={{ ...styles.button, ...(currentIndex === 0 || isSubmitting || isAutoSubmitting ? styles.buttonDisabled : {}) }}
                onClick={handlePrevious}
                disabled={currentIndex === 0 || isSubmitting || isAutoSubmitting}
              >
                Previous
              </Button>
              <Button
                className="custom-button glow-button"
                style={{ ...styles.button, ...(currentIndex === questions.length - 1 || isSubmitting || isAutoSubmitting ? styles.buttonDisabled : {}) }}
                onClick={handleNext}
                disabled={currentIndex === questions.length - 1 || isSubmitting || isAutoSubmitting}
              >
                Next
              </Button>
            </div>
            <Button
              className="custom-button submit-button glow-button"
              style={{ ...styles.button, ...(!isAllAnswered || isSubmitting || isAutoSubmitting ? styles.buttonDisabled : {}) }}
              onClick={() => setIsModalVisible(true)}
              disabled={!isAllAnswered || isSubmitting || isAutoSubmitting}
            >
              Submit
            </Button>
          </div>
        </div>
        {isAutoSubmitting && <div style={styles.loadingOverlay}><Spin size="large" tip="Auto-submitting test..." /></div>}
        <Modal
          title="Confirm Submission"
          open={isModalVisible}
          onOk={handleSubmit}
          okText="Submit Test"
          onCancel={() => setIsModalVisible(false)}
          cancelText="Continue Test"
          okButtonProps={{ style: { ...styles.modalButton, ...(isSubmitting ? styles.buttonDisabled : {}) }, className: 'glow-button', disabled: isSubmitting }}
          cancelButtonProps={{ style: styles.modalCancelButton, className: 'modal-cancel-button' }}
        >
          <p className="modal-text">Are you sure you want to submit the test? This action cannot be undone.</p>
        </Modal>
        <Modal
          title="Warning: Test Violation"
          open={violationModalVisible}
          onOk={handleViolationModalOk}
          okText="Continue Test"
          cancelButtonProps={{ style: { display: 'none' } }}
          closable={false}
          okButtonProps={{ style: styles.modalButton, className: 'glow-button' }}
        >
          <p className="modal-text">You have attempted to leave fullscreen mode. This is violation {violationCount} of {violationLimit}. After {violationLimit} violations, your test will be automatically submitted.</p>
        </Modal>
        <Modal
          title="Fullscreen Required"
          open={fullscreenPromptVisible}
          onOk={handleReenterFullscreen}
          okText="Enter Fullscreen"
          cancelButtonProps={{ style: { display: 'none' } }}
          closable={false}
          okButtonProps={{ style: styles.modalButton, className: 'glow-button' }}
        >
          <p className="modal-text">This test requires fullscreen mode to proceed. Please enter fullscreen mode to continue.{fullscreenError && ' If fullscreen mode is not working, ensure your browser allows fullscreen and the page is loaded over HTTPS.'}</p>
        </Modal>
      </div>
    </>
  );
};

export default TakeTestPage;